import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgClass } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SearchService } from '../../services/search.service';
import { SearchSegment } from '../../typings/search-segment';
import { SearchSecondaryNavItem } from '../../typings/search-secondary-nav-item';
import { AutoAnimateDirective } from '@planmeister/pithos';
import {
    faExcavator,
    faGrid2,
    faHelmetSafety,
    faMagnifyingGlass,
    faMound,
    faUserHelmetSafety
} from '@fortawesome/pro-light-svg-icons';
import { faTrafficCone } from '@fortawesome/pro-light-svg-icons/faTrafficCone';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-search-secondary-nav',
    imports: [
        NgClass,
        FaIconComponent,
        AutoAnimateDirective
    ],
    templateUrl: './search-secondary-nav.component.html',
    styleUrls: [ './search-secondary-nav.component.scss' ]
})
export class SearchSecondaryNavComponent {
    private readonly searchService = inject(SearchService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);

    protected readonly segment = this.searchService.select('segment');
    protected readonly mode = this.searchService.select('mode');
    protected readonly categoryId = this.searchService.select('categoryId');

    protected readonly items = computed<SearchSecondaryNavItem[]>(() => {
        const allItems: SearchSecondaryNavItem[] = [];
        if (this.categoryId()) {
            allItems.push({
                icon: faMagnifyingGlass,
                segment: null,
                subText: this.translateService.instant('modules.search.navigation.items.search.subText')
            });
        }

        if (this.mode() !== 'supply') {
            allItems.push({
                icon: faGrid2,
                segment: null,
                subText: this.translateService.instant('modules.search.navigation.items.all.subText')
            });
        }

        allItems.push(
            {
                icon: faHelmetSafety,
                segment: 'machines-bemand',
                disabled: this.mode() === 'companies',
                subText: this.translateService.instant('modules.search.navigation.items.machinesBemand.subText')
            },
            {
                icon: faExcavator,
                segment: 'machines-onbemand',
                disabled: this.mode() === 'companies',
                subText: this.translateService.instant('modules.search.navigation.items.machinesOnbemand.subText')
            },
            {
                icon: faUserHelmetSafety,
                segment: 'vakmensen',
                disabled: this.mode() === 'companies',
                subText: this.translateService.instant('modules.search.navigation.items.vakmensen.subText')
            },
            {
                icon: faMound,
                segment: 'grond-bouwstoffen',
                disabled: this.mode() === 'companies',
                subText: this.translateService.instant('modules.search.navigation.items.grondBouwstoffen.subText')
            },
            {
                icon: faTrafficCone,
                segment: 'materialen',
                disabled: this.mode() === 'companies',
                subText: this.translateService.instant('modules.search.navigation.items.materialen.subText')
            }
        );

        return allItems;
    });

    protected navigateToSegment(segment: SearchSegment | null): void {
        let route = '';
        switch (this.mode()) {
            case 'supply':
                route = 'aanbod';
                break;
            case 'demand':
                route = 'vraag';
                break;
            case 'companies':
                route = 'bedrijven';
                break;
            default:
                break;
        }

        if (segment === null) {
            this.searchService.clearFilters();
            if (this.mode() === 'supply' && this.categoryId()) {
                const input = document.getElementById('navbarCategorySearchInput');
                input!.focus();
            } else {
                this.router.navigate([ '/', route ], { replaceUrl: true });
            }
        } else {
            this.searchService.set('categoryId', null);
            this.searchService.set('date', null);

            this.router.navigate([ '/', route ], {
                queryParams: { segment },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });

            this.searchService.set('segment', segment);
        }
    }
}
